import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { StatusBadge, statusToTone } from "../components/shell/StatusBadge";
import { useToast } from "../components/shell/Toast";
import { Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/packages/utils/cn";
import { MarkdownEditor } from "../components/release/MarkdownEditor";

const COMMITS_PER_PAGE = 10;

interface Commit {
  id: string;
  sha: string;
  message: string;
  author: string;
  repoName: string;
  repoUrl: string;
  committedAt: string;
}

interface Audience {
  id: string;
  name: string;
  description: string | null;
  color: string;
  emailEnabled: boolean;
  emailRecipients: string[];
  slackEnabled: boolean;
  slackChannelId: string | null;
  slackChannelName: string | null;
}

interface AudienceContent {
  id: string;
  releaseId: string;
  audienceId: string;
  content: string;
  enabled: boolean;
  audience: Audience;
}

interface Release {
  id: string;
  title: string;
  content: string;
  status: string;
  publishedAt: string | null;
  commitCount: number;
  errorMessage?: string | null;
  commits: Commit[];
  audienceContents: AudienceContent[];
}

export function ReleaseDetailPage() {
  const { teamId, projectId, releaseId } = useParams();
  const { teams, projects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find((p) => p.referenceId === projectId);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [release, setRelease] = useState<Release | null>(null);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [savingTitle, setSavingTitle] = useState(false);
  const [savingBody, setSavingBody] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  const [commitsPage, setCommitsPage] = useState(0);
  const [sending, setSending] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [hasRepoConnected, setHasRepoConnected] = useState(true);

  const refresh = () => {
    if (!projectId || !releaseId) return;
    fetch(`/api/projects/${projectId}/releases/${releaseId}`)
      .then((r) => r.json())
      .then((d: Release) => {
        setRelease(d);
        setTitleDraft(d.title);
        setBodyDraft(d.content);
      });
  };

  useEffect(() => {
    setCommitsPage(0);
    refresh();
    if (!projectId) return;
    fetch(`/api/projects/${projectId}/audiences`)
      .then((r) => r.json())
      .then(setAudiences);
    fetch(`/api/projects/${projectId}/github-connections`)
      .then((r) => r.json())
      .then((d: unknown[]) => setHasRepoConnected(Array.isArray(d) && d.length > 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, releaseId]);

  // poll while processing
  useEffect(() => {
    if (release?.status !== "processing") return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release?.status]);

  // Debounced auto-save — fires 700ms after the user stops typing.
  useEffect(() => {
    if (!release) return;
    if (release.status !== "draft") return;
    if (bodyDraft === release.content) return;
    const t = setTimeout(saveBody, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyDraft]);

  if (!team || !project || !release) return null;

  const saveTitle = async () => {
    setSavingTitle(true);
    await fetch(`/api/projects/${projectId}/releases/${releaseId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: titleDraft }),
    });
    setSavingTitle(false);
    refresh();
  };

  const saveBody = async () => {
    setSavingBody(true);
    await fetch(`/api/projects/${projectId}/releases/${releaseId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: bodyDraft }),
    });
    setSavingBody(false);
    refresh();
  };

  // Optimistic — flip the local state immediately, then fire the request.
  // If it fails, roll back and surface a toast. We synthesize an
  // audienceContent entry when toggling on for the first time so the UI
  // stays consistent before the server-created row arrives.
  const toggleAudienceEnabled = (audienceId: string, enabled: boolean) => {
    setRelease((prev) => {
      if (!prev) return prev;
      const exists = prev.audienceContents.some(
        (c) => c.audienceId === audienceId,
      );
      const audience = audiences.find((a) => a.id === audienceId);
      const next = exists
        ? prev.audienceContents.map((c) =>
            c.audienceId === audienceId ? { ...c, enabled } : c,
          )
        : audience
          ? [
              ...prev.audienceContents,
              {
                id: `optimistic-${audienceId}`,
                releaseId: prev.id,
                audienceId,
                content: prev.content,
                enabled,
                audience,
              },
            ]
          : prev.audienceContents;
      return { ...prev, audienceContents: next };
    });

    fetch(
      `/api/projects/${projectId}/releases/${releaseId}/audiences/${audienceId}/content`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      },
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to update audience");
      })
      .catch((err) => {
        toast({
          tone: "error",
          title: "Couldn't update audience",
          description: err instanceof Error ? err.message : "Network error",
        });
        refresh();
      });
  };

  const sendRelease = async () => {
    setSending(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/releases/${releaseId}/send`,
        { method: "POST" },
      );
      const data = (await res.json()) as
        | { dispatched: number; errors: string[] }
        | { error: string };
      if (!res.ok || "error" in data) {
        toast({
          tone: "error",
          title: "Send failed",
          description:
            "error" in data ? data.error : "Server returned an error",
        });
      } else if (data.errors.length === 0) {
        toast({
          tone: "success",
          title: "Release sent",
          description: `Dispatched to ${data.dispatched} channel${data.dispatched === 1 ? "" : "s"}.`,
        });
      } else {
        toast({
          tone: data.dispatched > 0 ? "info" : "error",
          title:
            data.dispatched > 0
              ? `Sent with ${data.errors.length} error${data.errors.length === 1 ? "" : "s"}`
              : "Send failed",
          description: data.errors.slice(0, 2).join(" · "),
        });
      }
      refresh();
    } catch (err) {
      toast({
        tone: "error",
        title: "Send failed",
        description: err instanceof Error ? err.message : "Network error",
      });
    } finally {
      setSending(false);
    }
  };

  const publishRelease = async () => {
    if (bodyDraft !== release.content) {
      await saveBody();
    }
    setPublishing(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/releases/${releaseId}/publish`,
        { method: "POST" },
      );
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error ?? "Publish failed");
      }
      refresh();
      toast({ tone: "success", title: "Release published" });
    } catch (err) {
      toast({
        tone: "error",
        title: "Publish failed",
        description: err instanceof Error ? err.message : "Network error",
      });
    } finally {
      setPublishing(false);
    }
  };

  const refetchCommits = async () => {
    setRefetching(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/releases/${releaseId}/refetch-commits`,
        { method: "POST" },
      );
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        throw new Error(data.error ?? "Refetch failed");
      }
      const data = (await res.json()) as { count: number };
      toast({
        tone: "success",
        title: `Pulled ${data.count} commit${data.count === 1 ? "" : "s"}`,
      });
      refresh();
    } catch (err) {
      toast({
        tone: "error",
        title: "Refetch failed",
        description: err instanceof Error ? err.message : "Network error",
      });
    } finally {
      setRefetching(false);
    }
  };

  const discardRelease = async () => {
    if (!confirm("Discard this release? This cannot be undone.")) return;
    setDiscarding(true);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/releases/${releaseId}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error("Delete failed");
      navigate(`/teams/${teamId}/projects/${projectId}/releases`);
    } catch (err) {
      toast({
        tone: "error",
        title: "Discard failed",
        description: err instanceof Error ? err.message : "Network error",
      });
      setDiscarding(false);
    }
  };

  const enabledAudiences = release.audienceContents.filter((c) => c.enabled);
  const commitPageCount = Math.max(
    1,
    Math.ceil(release.commits.length / COMMITS_PER_PAGE),
  );
  const safeCommitsPage = Math.min(commitsPage, commitPageCount - 1);
  const visibleCommits = release.commits.slice(
    safeCommitsPage * COMMITS_PER_PAGE,
    (safeCommitsPage + 1) * COMMITS_PER_PAGE,
  );

  return (
    <>
      <Topbar
        title={release.title}
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project.name, to: `/teams/${team.id}/projects/${project.referenceId}` },
          {
            label: "Releases",
            to: `/teams/${team.id}/projects/${project.referenceId}/releases`,
          },
          { label: release.title },
        ]}
        actions={
          <>
            <StatusBadge tone={statusToTone(release.status)}>
              {release.status}
            </StatusBadge>
            <Link
              to={`/teams/${teamId}/projects/${projectId}/releases/settings`}
            >
              <Button size="sm" variant="outline">
                Settings
              </Button>
            </Link>
            {release.status === "draft" && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={discardRelease}
                  disabled={discarding}
                >
                  {discarding ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Discarding…
                    </>
                  ) : (
                    "Discard"
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={publishRelease}
                  disabled={publishing}
                >
                  {publishing ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      Publishing…
                    </>
                  ) : (
                    "Publish"
                  )}
                </Button>
              </>
            )}
            {release.status === "published" && (
              <Button
                size="sm"
                onClick={sendRelease}
                disabled={sending || enabledAudiences.length === 0}
              >
                {sending ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Sending…
                  </>
                ) : (
                  "Send release →"
                )}
              </Button>
            )}
          </>
        }
      />

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
        {/* Editor */}
        <div className="flex-1 overflow-auto min-w-0">
          <div className="max-w-3xl mx-auto px-6 sm:px-12 py-8">
            <div className="text-eyebrow mb-3">
              {release.commitCount} commits ·{" "}
              {format(
                new Date(release.publishedAt ?? Date.now()),
                "MMMM do 'of' yyyy",
              )}
            </div>
            <Input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => titleDraft !== release.title && saveTitle()}
              className="!text-3xl !h-auto !py-2 !px-1 font-sans font-semibold tracking-tight border-0 focus-visible:ring-0 focus-visible:bg-panel/40 mb-4 bg-transparent"
              disabled={savingTitle}
            />

            {release.status === "processing" ? (
              <div className="px-2 py-12 text-sm text-muted-foreground italic">
                Generating release notes…
              </div>
            ) : (
              <MarkdownEditor
                key={`${release.id}:${release.status}`}
                initialMarkdown={release.content}
                onChange={setBodyDraft}
                placeholder="Write your release notes here…"
                readOnly={release.status === "published"}
              />
            )}
            {release.errorMessage && (
              <div className="mt-3 text-sm text-signal-red">
                {release.errorMessage}
              </div>
            )}
          </div>
        </div>

        {/* Audiences rail */}
        <aside className="w-full md:w-[320px] md:shrink-0 border-t md:border-t-0 md:border-l border-rule bg-panel/40 overflow-auto flex flex-col">
          <div className="px-5 py-4 border-b border-rule bg-paper">
            <div className="text-eyebrow mb-1">
              Going to · {enabledAudiences.length} audiences
            </div>
            <div className="font-serif font-semibold text-lg">
              {enabledAudiences.reduce(
                (sum, c) => sum + c.audience.emailRecipients.length,
                0,
              )}{" "}
              recipients
            </div>
          </div>
          <div className="px-3 py-3 space-y-2">
            {audiences.length === 0 && (
              <div className="text-xs text-muted-foreground italic px-2">
                No audiences yet. Open Settings to create one.
              </div>
            )}
            {audiences.map((aud) => {
              const content = release.audienceContents.find(
                (c) => c.audienceId === aud.id,
              );
              const enabled = content?.enabled ?? false;
              return (
                <div
                  key={aud.id}
                  className={cn(
                    "bg-card-paper border rounded-md p-3 transition-opacity",
                    enabled ? "border-rule" : "border-rule-soft opacity-60",
                  )}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="size-2 rounded-sm"
                      style={{ background: aud.color }}
                    />
                    <span className="text-sm font-semibold flex-1 truncate">
                      {aud.name}
                    </span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) =>
                        toggleAudienceEnabled(aud.id, v)
                      }
                    />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {aud.emailEnabled && aud.emailRecipients.length > 0 && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-panel border border-rule-soft">
                        email · {aud.emailRecipients.length}
                      </span>
                    )}
                    {aud.slackEnabled && aud.slackChannelName && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-full bg-panel border border-rule-soft">
                        #{aud.slackChannelName}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            <Link
              to={`/teams/${teamId}/projects/${projectId}/releases/settings?tab=audiences`}
              className="block text-center text-xs text-signal-red font-semibold py-2 border border-dashed border-rule rounded-md hover:bg-panel/40"
            >
              + Add audience
            </Link>
          </div>

          <div className="px-5 py-4 border-y border-rule bg-paper">
            <div className="text-eyebrow">
              Commits · {release.commits.length}
            </div>
          </div>
          <div className="px-3 py-3 space-y-1 font-mono text-xs">
            {release.commits.length === 0 ? (
              release.commitCount > 0 ? (
                <div className="text-xs px-2 font-sans space-y-2">
                  <div className="text-muted-foreground italic">
                    Commit list missing for this release.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={refetchCommits}
                    disabled={refetching}
                  >
                    {refetching ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        Refetching…
                      </>
                    ) : (
                      "Refetch from GitHub"
                    )}
                  </Button>
                </div>
              ) : !hasRepoConnected ? (
                <div className="text-xs px-2 font-sans space-y-2">
                  <div className="text-muted-foreground italic">
                    No GitHub repo connected. Connect one to pull in commits,
                    or keep editing this release manually.
                  </div>
                  <a
                    href={`/api/projects/${projectId}/github/connect?teamId=${teamId}`}
                  >
                    <Button size="sm" variant="outline">
                      Connect GitHub
                    </Button>
                  </a>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic px-2 font-sans">
                  No commits in this release.
                </div>
              )
            ) : (
              <>
                {visibleCommits.map((c) => (
                  <a
                    key={c.id}
                    href={`${c.repoUrl}/commit/${c.sha}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grid grid-cols-[8ch_1fr_12ch] gap-3 px-2 py-1 rounded hover:bg-panel/50"
                  >
                    <span className="text-faint truncate">
                      {c.sha.slice(0, 7)}
                    </span>
                    <span className="truncate">
                      {c.message.split("\n")[0]}
                    </span>
                    <span className="text-faint truncate text-right">
                      {c.author}
                    </span>
                  </a>
                ))}
                {commitPageCount > 1 && (
                  <div className="flex items-center justify-between px-1 pt-2 mt-1 border-t border-rule-soft font-sans">
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={safeCommitsPage === 0}
                      onClick={() =>
                        setCommitsPage((p) => Math.max(0, p - 1))
                      }
                    >
                      ← Prev
                    </Button>
                    <span className="text-faint font-mono text-[11px]">
                      {safeCommitsPage + 1} / {commitPageCount}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={safeCommitsPage >= commitPageCount - 1}
                      onClick={() =>
                        setCommitsPage((p) =>
                          Math.min(commitPageCount - 1, p + 1),
                        )
                      }
                    >
                      Next →
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </aside>
      </div>

    </>
  );
}
